import { useState, useEffect, useCallback, useRef } from 'react'

import { isEqual } from 'lodash'

type HighlightPos = {
  offsetX: number
  offsetY: number
  width: number
  height: number
}

const useHighlight = (selectedValue: string) => {
  const memoedTarget = useRef<string>(undefined)
  const memoedPosition = useRef<HighlightPos>(undefined)

  const [highlightPos, setHighlightPos] = useState<HighlightPos>()

  const calcHighlightedPos = useCallback(() => {
    const all = document.getElementsByClassName(`highlight-${selectedValue}`)
    const element = all.length === 1 ? all[0] : null

    if (!element) {
      setHighlightPos(undefined)
      return
    }

    const el = element as HTMLElement

    const rect = el.getBoundingClientRect()

    const offsetX = el.offsetLeft
    const offsetY = el.offsetTop
    const width = rect.width
    const height = rect.height

    const newPos: HighlightPos = { offsetX, offsetY, width, height }

    // if the target has changed, update the position instantly
    if (memoedTarget.current && memoedTarget.current !== selectedValue) {
      memoedPosition.current = newPos
      memoedTarget.current = selectedValue
      setHighlightPos({ offsetX, offsetY, width, height })
      return
    }

    // if the position is different, i.e. the element is currently moving or resizing, wait a bit and recalculate
    // this can happen when tabs are in a modal that animated in
    if (!isEqual(memoedPosition.current, newPos)) {
      memoedPosition.current = newPos
      setTimeout(() => calcHighlightedPos(), 100)
    } else {
      setHighlightPos({ offsetX, offsetY, width, height })
    }
  }, [selectedValue])

  useEffect(() => {
    // only calculate the position when the target changes
    if (memoedTarget.current !== selectedValue) {
      calcHighlightedPos()
    }

    window.addEventListener('resize', () => calcHighlightedPos)

    return () => {
      window.removeEventListener('resize', calcHighlightedPos)
    }
  }, [calcHighlightedPos, selectedValue])

  return {
    highlightPos,
    recalculate: calcHighlightedPos,
  }
}

export default useHighlight
